import React, { useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  PlaylistProvider,
  usePlaylist,
} from "../../../context/PlaylistContext";
import { ScheduledCreativesProvider } from "../../../context/ScheduledCreativesContext";
import { TagProvider } from "../../../context/TagContext";
import { ScreenProvider } from "../../../context/ScreenContext";
import CreatePlaylistScreen from "./playlistscreens/CreatePlaylistScreen";
import PlaylistListScreen from "./playlistscreens/PlaylistListScreen";
import DefaultEmptyStateView from "./playlistscreens/AddContainScreens/DefaultEmptyStateView";
import ContentSelectionPage from "./playlistscreens/AddContainScreens/ContentSelectionPage";

// Component to handle conditional routing logic
const PlaylistRouter: React.FC = () => {
  const { playlists } = usePlaylist();
  const location = useLocation();
  const navigate = useNavigate();
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize the router after playlists are loaded
  useEffect(() => {
    // Add a small delay to ensure playlists context is fully loaded
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [playlists]);

  // Handle navigation debugging
  useEffect(() => {
    console.log("=== PLAYLIST ROUTER DEBUG ===");
    console.log("Current location:", location.pathname);
    console.log("Playlists count:", playlists.length);
    console.log("Is initialized:", isInitialized);
  }, [location.pathname, playlists.length, isInitialized]);

  // Show loading state until initialized
  if (!isInitialized) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Redirect root to appropriate screen based on playlist count */}
      <Route
        path="/"
        element={
          playlists.length === 0 ? (
            <Navigate to="create-playlist" replace />
          ) : (
            <Navigate to="list" replace />
          )
        }
      />

      {/* Always allow access to create playlist screen */}
      <Route path="create-playlist" element={<CreatePlaylistScreen />} />

      {/* Always allow access to playlist list screen */}
      <Route path="list" element={<PlaylistListScreen />} />

      {/* Individual playlist route - handles only existing playlists */}
      <Route path="playlist/:id" element={<DefaultEmptyStateView />} />

      {/* Content Selection Page - handles only existing playlists */}
      <Route
        path="playlist/:id/add-content"
        element={<ContentSelectionPage />}
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Enhanced Playlist Provider wrapper with ALL required providers
const PlaylistsWithProvider: React.FC = () => {
  return (
    <ScheduledCreativesProvider>
      <TagProvider>
        <ScreenProvider>
          <PlaylistProvider>
            <div className="h-full">
              <PlaylistRouter />
            </div>
          </PlaylistProvider>
        </ScreenProvider>
      </TagProvider>
    </ScheduledCreativesProvider>
  );
};

const Playlists: React.FC = () => {
  return <PlaylistsWithProvider />;
};

export default Playlists;
